import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, XCircle, ArrowRight, Zap, Shield, Clock, 
  FileText, Bell, BarChart3, Calculator, CreditCard, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SmartFactureVsIndy = () => {
  const comparisons = [
    { feature: "Format Factur-X conforme 2026", smartfacture: true, competitor: false },
    { feature: "Relances automatiques impayés", smartfacture: true, competitor: true },
    { feature: "Scanner IA de factures", smartfacture: true, competitor: false },
    { feature: "Module URSSAF intégré", smartfacture: true, competitor: false },
    { feature: "Rapprochement bancaire IA", smartfacture: true, competitor: true },
    { feature: "Export comptable CSV", smartfacture: true, competitor: true },
    { feature: "Dashboard temps réel", smartfacture: true, competitor: true },
    { feature: "Support prioritaire", smartfacture: true, competitor: true },
    { feature: "Essai gratuit sans CB", smartfacture: true, competitor: false },
    { feature: "À partir de 14€/mois", smartfacture: true, competitor: false },
  ];

  const smartfactureAdvantages = [
    {
      icon: FileText,
      title: "Factur-X natif",
      description: "SmartFacture génère des factures au format Factur-X (PDF/A-3 + XML) dès maintenant, prêt pour 2026. Indy n'offre pas encore ce format."
    },
    {
      icon: Sparkles,
      title: "Scanner IA intelligent",
      description: "Photographiez vos factures fournisseurs, l'IA extrait automatiquement toutes les données. Une fonctionnalité absente chez Indy."
    },
    {
      icon: Calculator,
      title: "Module URSSAF intégré",
      description: "Simulez vos cotisations, anticipez vos charges. Un outil unique que vous ne trouverez pas chez Indy."
    },
    {
      icon: CreditCard,
      title: "Prix plus accessible",
      description: "À partir de 14€/mois avec 7 jours d'essai gratuit sans carte bancaire. Indy démarre plus cher avec engagement."
    }
  ];

  const faqs = [
    {
      q: "SmartFacture AI est-il meilleur qu'Indy ?",
      a: "SmartFacture AI se distingue par son format Factur-X natif (obligatoire en 2026), son scanner IA de factures, et son module URSSAF intégré. Ces fonctionnalités ne sont pas disponibles chez Indy."
    },
    {
      q: "Puis-je migrer d'Indy vers SmartFacture AI ?",
      a: "Oui ! Vous pouvez importer vos données existantes. Notre équipe vous accompagne gratuitement dans la migration."
    },
    {
      q: "SmartFacture AI est-il adapté aux freelances ?",
      a: "Absolument. SmartFacture AI a été conçu pour les freelances, auto-entrepreneurs et TPE françaises, avec toutes les mentions légales et le format conforme."
    },
    {
      q: "Quel est le prix de SmartFacture AI vs Indy ?",
      a: "SmartFacture AI démarre à 14€/mois (Starter), contre environ 24€/mois pour Indy. De plus, SmartFacture offre 7 jours d'essai gratuit sans carte bancaire."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>SmartFacture AI vs Indy : Comparatif 2026 | Quelle solution choisir ?</title>
        <meta name="description" content="Comparatif détaillé SmartFacture AI vs Indy. Découvrez les différences de fonctionnalités, prix et conformité e-facturation 2026. Faites le bon choix pour votre entreprise." />
        <link rel="canonical" href="https://smartfacture-ai.com/comparaison/smartfacture-vs-indy" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Essai gratuit
              </Button>
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              Comparatif 2026
            </Badge>
            <h1 className="text-3xl lg:text-5xl font-bold mb-6">
              SmartFacture AI vs Indy
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Quelle solution de facturation choisir pour être conforme à la réforme e-facturation 2026 ? Découvrez notre comparatif détaillé.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Comparison Table */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Tableau comparatif des fonctionnalités
            </h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-3 bg-slate-100 p-4 font-semibold text-slate-700">
                <div>Fonctionnalité</div>
                <div className="text-center text-blue-600">SmartFacture AI</div>
                <div className="text-center">Indy</div>
              </div>
              {comparisons.map((item, idx) => (
                <div key={idx} className={`grid grid-cols-3 p-4 items-center ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                  <div className="text-slate-700">{item.feature}</div>
                  <div className="text-center">
                    {item.smartfacture ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                    )}
                  </div>
                  <div className="text-center">
                    {item.competitor ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Advantages */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Pourquoi choisir SmartFacture AI plutôt qu'Indy ?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {smartfactureAdvantages.map((adv, idx) => {
                const Icon = adv.icon;
                return (
                  <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{adv.title}</h3>
                    <p className="text-slate-600">{adv.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl p-8 lg:p-12 text-white text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Prêt à passer à SmartFacture AI ?
            </h2>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
              Rejoignez les entrepreneurs qui ont choisi la conformité 2026 et l'automatisation intelligente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 hover:bg-blue-50 font-semibold rounded-xl transition-colors"
              >
                Essai gratuit 7 jours
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link 
                to="/demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white text-white hover:bg-white/10 font-semibold rounded-xl transition-colors"
              >
                Voir la démo
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Other Comparisons */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 text-center mb-6">
              Autres comparatifs
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/comparaison/smartfacture-vs-freebe" className="text-blue-600 hover:underline">
                SmartFacture vs Freebe
              </Link>
              <Link to="/comparaison/smartfacture-vs-tiime" className="text-blue-600 hover:underline">
                SmartFacture vs Tiime
              </Link>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SmartFactureVsIndy;
